/**
 * Login context for authentication state management
 */

import { createContext, useContext, useState, useCallback, useEffect } from 'react';
import PropTypes from 'prop-types';
import matrixAuthService from '../services/matrixAuthService';
import indexedDBService from '../services/indexedDB';
import notificationService from '../services/notificationService';
import { cacheSsoSession } from '../services/authCredentialService';
import { DEFAULT_HOMESERVER } from '../config/homeserver';
import logger from '../utils/logger';
import { generateInitialsAvatar } from '../utils/avatar';
import fhirService from '../services/fhirService';
import matrixService from '../services/matrixService';
import timInformationService from '../services/timInformationService';
import * as credentialStore from '../services/credentialStore';
import { isSupersededInvalidation } from '../utils/sessionInvalidation';
// import { ModuleRunner } from './ModuleRunner';

// Poll interval (ms) used while waiting for matrixService to publish its
// singleton client after authentication. The MatrixClientProvider that owns
// the client lives deeper in the tree than LoginProvider, so we cannot
// observe its readiness through React context here.
// Exported so tests can pin their wait windows to the same constant rather
// than guessing a magic number.
export const MATRIX_CLIENT_POLL_INTERVAL_MS = 200;

// Upper bound (ms) on EACH restore-time token check. NOTE: this is the per-phase
// CEILING, not the typical cost. A healthy cold start THAT RESTORES A STORED TOKEN
// pays one whoami round-trip before the app renders (the restore gate awaits
// validation before clearing isLoading), where the pre-validation restore took
// zero round-trips. (The SSO-pending and session-invalidated paths return before
// validation and pay none.) On an
// EXPIRED-but-refreshable access token the gate pays a second round-trip (the
// /refresh), so the worst-case startup wait on a flaky-but-reachable server is
// two windows (whoami, then refresh) — acceptable on the unhappy path. The RTT
// is the deliberate cost of not stranding a dead/corrupted token on
// /#/conversations. If the homeserver does not answer within a window we treat
// the result as indeterminate and restore optimistically, so a slow/unreachable
// network at startup never blocks the app indefinitely or locks out a valid
// session. Exported so tests can pin their wait windows here.
export const TOKEN_VALIDATION_TIMEOUT_MS = 8000;

const LoginContext = createContext();

export const useLogin = () => {
  const context = useContext(LoginContext);
  if (!context) {
    throw new Error('useLogin must be used within LoginProvider');
  }
  return context;
};

export const LoginProvider = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  // Tri-state homeserver classification for the VZD search privacy filter:
  //   null  → still resolving (block insured-only filter to avoid race)
  //   true  → insured-person homeserver, must drop hide-versicherte endpoints
  //   false → HCP/non-insured homeserver, no client-side filter required
  const [isInsuredHomeserver, setIsInsuredHomeserver] = useState(null);

  // Check for existing login state on app start
  useEffect(() => {
    // A DEFINITIVE access-token rejection (401) does NOT mean the session is
    // dead — the access token may have merely EXPIRED (Matrix soft-logout) while
    // the refresh token is still valid. The running client refreshes silently in
    // exactly this case (matrixService.js tokenRefreshFunction), so the restore
    // gate must attempt the same before tearing down; otherwise every
    // refresh-token session (SSO especially, whose OIDC access tokens are
    // short-lived) gets force-logged-out on startup whenever its access token
    // has aged out. Bounded by the same timeout so a slow /refresh can't hang
    // startup. Returns 'valid' (refresh succeeded — fresh creds re-persisted by
    // matrixAuthService.storeCredentials), 'invalid' (refresh token also
    // rejected with 401 → session truly dead; the caller's 'invalid' branch
    // performs the single teardown), or 'indeterminate' (transient refresh
    // failure/timeout → stay optimistic; the running client retries — never a
    // false lockout).
    const refreshRestoredSession = async (creds) => {
      let timer;
      try {
        const timeout = new Promise((resolve) => {
          timer = setTimeout(() => resolve({ outcome: 'timeout' }), TOKEN_VALIDATION_TIMEOUT_MS);
        });
        const refreshed = await Promise.race([
          // suppressInvalidationSideEffects: WE own teardown here (the 'invalid'
          // verdict runs logout + shows the login form). Tell refreshToken not to
          // run its mid-session invalidation pipeline on a 401 — no duplicate
          // logout, and no "session expired" modal on a routine cold-start relaunch.
          matrixAuthService.refreshToken(creds.refreshToken, creds.homeserverUrl, { suppressInvalidationSideEffects: true, userId: creds.userId, deviceId: creds.deviceId })
            .then((r) => ({ outcome: r?.accessToken ? 'ok' : 'transient' }))
            .catch((err) => ({ outcome: err?.httpStatus === 401 ? 'rejected' : 'transient' })),
          timeout,
        ]);
        switch (refreshed?.outcome) {
          case 'ok':
            logger.debug('LoginContext: access token expired but refresh succeeded — session restored');
            return 'valid';
          case 'rejected':
            logger.warn('LoginContext: refresh token also rejected (401) — clearing session');
            return 'invalid';
          default: // 'transient' | 'timeout': don't lock out a recoverable session
            logger.debug('LoginContext: token refresh indeterminate, staying optimistic:', refreshed?.outcome);
            return 'indeterminate';
        }
      } finally {
        if (timer) clearTimeout(timer);
      }
    };

    // Validate restored credentials against the homeserver (whoami), bounded by
    // a timeout. Returns:
    //   'valid'          — token accepted; restore the session
    //   'invalid'        — DEFINITIVE rejection (M_UNKNOWN_TOKEN / M_MISSING_TOKEN
    //                      / HTTP 401) AND the refresh token can't recover it:
    //                      the session is dead, tear it down
    //   'indeterminate'  — network error / timeout: stay optimistic (a transient
    //                      blip must not lock out a valid session)
    const validateRestoredSession = async (creds) => {
      let timer;
      try {
        const timeout = new Promise((resolve) => {
          timer = setTimeout(() => resolve({ valid: false, code: '__timeout__' }), TOKEN_VALIDATION_TIMEOUT_MS);
        });
        const result = await Promise.race([
          matrixAuthService.validateCredentials(creds),
          timeout,
        ]);
        if (result?.valid) return 'valid';
        const definitive = result?.code === 'M_UNKNOWN_TOKEN'
          || result?.code === 'M_MISSING_TOKEN'
          || result?.httpStatus === 401;
        if (!definitive) return 'indeterminate';
        // Access token definitively rejected: try the refresh token before
        // tearing down (see refreshRestoredSession). Only when there is no
        // refresh token to fall back on is the rejection terminal.
        if (!creds?.refreshToken) return 'invalid';
        return await refreshRestoredSession(creds);
      } catch (err) {
        // validateCredentials is not expected to throw, but a validation error
        // must never block startup — treat as indeterminate (optimistic restore).
        logger.debug('LoginContext: token validation error, treating as indeterminate:', err);
        return 'indeterminate';
      } finally {
        if (timer) clearTimeout(timer);
      }
    };

    const checkLoginState = async () => {
      let ssoDeferred = false;
      try {
        // Drop any cached homeserver classifications from the previous user's
        // session before we restore credentials. The cache is keyed by
        // serverName, so without this clear a deployment hot-swap would
        // re-use a stale value.
        timInformationService.clearCache();

        // Check if session was invalidated during initialization (race condition fix)
        let invalidatedData = null;
        try {
          invalidatedData = sessionStorage.getItem('matrix:session-invalidated');
        } catch (e) {
          // sessionStorage unavailable (private browsing, storage disabled)
          logger.debug('Could not read session invalidation flag:', e);
        }
        if (invalidatedData) {
          // NED-4397: an SSO callback is a FULL PAGE RELOAD, so a `matrix:session-invalidated`
          // flag present at this boot belongs to a PRIOR session on this tab (sessionStorage
          // survives same-tab navigations) and the in-memory nonce reset to 0 (the nonce guard
          // can't help). Clear it — and FALL THROUGH so the F-06 force-clear (below) and
          // indexedDBService.init() still run — ONLY for a positively CSRF-validated SSO
          // callback. SSOCallbackHandler sets window.__validatedSSOCallback after
          // validateAndConsumeCSRFState() succeeds; its child effect runs before this parent
          // effect. This is FAIL-CLOSED: a missing/invalid/unsolicited callback (or,
          // hypothetically, a changed effect order) leaves the signal unset → the ELSE branch
          // shows the modal, never suppressing a genuine logout. Do NOT gate on
          // window.__pendingSSOToken — SSOCallbackHandler nulls it before this effect runs.
          if (window.__validatedSSOCallback) {
            // Consume-once (mirror the __pendingSSOToken scrub at SSOCallbackHandler.jsx:60):
            // clear the signal as we read it so a future re-run of this effect in the SAME
            // document (a soft LoginProvider re-mount, or a dev StrictMode double-invoke)
            // can't see a leftover `true` and suppress a GENUINE invalidation. Absence of the
            // signal is fail-closed — the ELSE branch shows the modal.
            window.__validatedSSOCallback = false;
            logger.info('LoginContext: clearing stale session-invalidation flag (validated SSO callback)');
            try { sessionStorage.removeItem('matrix:session-invalidated'); } catch { /* ignore */ }
          } else {
            logger.info('LoginContext: Session invalidation flag detected, clearing auth state');
            // DON'T remove the flag here - let LoginPage show the modal first
            setIsAuthenticated(false);
            setCurrentUser(null);
            setIsLoading(false);
            return; // Don't restore credentials - session is invalid
          }
        }

        // If SSO callback is pending, defer loading reset to SSOCallbackHandler
        if (window.__pendingSSOToken) {
          logger.debug('LoginContext: SSO token pending, deferring to SSOCallbackHandler');
          ssoDeferred = true;
          // Safety net: reset loading when SSOCallbackHandler completes (any outcome)
          const resetLoading = () => {
            setIsLoading(false);
            window.removeEventListener('sso:callback-complete', resetLoading);
          };
          window.addEventListener('sso:callback-complete', resetLoading);
          // Final fallback timeout (15s) in case event never fires
          setTimeout(() => {
            window.removeEventListener('sso:callback-complete', resetLoading);
            setIsLoading(false);
          }, 15000);
          return;
        }

        logger.debug('LoginContext: Checking for existing login state...');

        // F-06 boot safety net: if a prior logout's IndexedDB deletion was
        // blocked, the matrixForceClearStores flag is set. Delete the app-owned
        // conversation store (TIConversationsApp) by constant name BEFORE
        // re-opening it, so residue never survives a blocked logout-delete.
        // Flag-gated, so a HEALTHY reload never wipes the user's data.
        //
        // The sibling ti-m-media-cache DB is wiped symmetrically inside
        // MediaCacheService's own open path (init() -> _openWithBootCleanup(),
        // same flag gate). That singleton is constructed at module-eval time
        // (App.jsx statically imports ChatExpiryReminderModal -> mediaCacheService),
        // i.e. before this useEffect runs — so its wipe must live in the service,
        // since a bootstrap-level wipe here could not beat its constructor open.
        //
        // The flag is intentionally LEFT set here for matrixService.createClient()
        // to consume (it also drives crypto-store clearing on the next login).
        try {
          if (localStorage.getItem('matrixForceClearStores') === 'true') {
            logger.info('LoginContext: force-clear flag set — wiping residual app DB before init');
            await indexedDBService.deleteDatabase();
          }
        } catch (error) {
          logger.warn('LoginContext: boot-time app DB wipe failed (continuing):', error);
        }

        // Initialize IndexedDB
        try {
          await indexedDBService.init();
          logger.info('IndexedDB initialized successfully');
        } catch (error) {
          logger.error('Failed to initialize IndexedDB:', error);
          // App can still work without cache
        }

        // Check for Matrix credentials in localStorage (via the single owner).
        const matrixCreds = credentialStore.readCredentials();
        if (matrixCreds) {
          try {
            logger.debug('LoginContext: Found Matrix credentials in localStorage for user:', matrixCreds.userId);

            // Create user object from Matrix credentials
            const userName = matrixCreds.userId.split(':')[0].substring(1); // Extract username from @user:server
            const user = {
              id: matrixCreds.userId,
              name: userName || matrixCreds.userId,
              username: matrixCreds.userId,
              access_token: matrixCreds.accessToken,
              homeserver: matrixCreds.homeserverUrl,
              avatar: generateInitialsAvatar(userName || matrixCreds.userId), // Generate avatar from initials
            };

            // RC2: validate the restored token BEFORE declaring authenticated.
            // Trusting matrixCreds' presence alone strands a corrupted/revoked
            // token on /#/conversations (the login form never renders), e.g.
            // after a mid-login network blip corrupts the stored macaroon or a
            // server-side logout/all deletes the device. Only a DEFINITIVE
            // rejection clears the session; a transient/indeterminate result
            // restores optimistically (a blip must not lock out a valid session).
            const verdict = await validateRestoredSession(matrixCreds);
            if (verdict === 'invalid') {
              logger.warn('LoginContext: stored Matrix token rejected by homeserver — clearing session, showing login form');
              try {
                await matrixAuthService.logout({ skipServerLogout: true });
              } catch (cleanupErr) {
                logger.debug('LoginContext: cleanup after invalid token failed (continuing):', cleanupErr);
                credentialStore.clearCredentials();
              }
              setCurrentUser(null);
              setIsAuthenticated(false);
            } else {
              setCurrentUser(user);
              setIsAuthenticated(true);
              logger.debug('LoginContext: User restored from Matrix credentials (token:', verdict, ')');
            }
          } catch (error) {
            logger.error('LoginContext: Error restoring Matrix credentials:', error);
            credentialStore.clearCredentials();
          }
        } else {
          logger.debug('LoginContext: No valid login state found');
        }
      } catch (error) {
        logger.error('LoginContext: Error checking login state:', error);
      } finally {
        if (!ssoDeferred) {
          setIsLoading(false);
        }
      }
    };

    checkLoginState();
  }, []);

  // Listen for session invalidation events (handles runtime session invalidation)
  // This listener is registered early (before MatrixClientProvider init) so it catches
  // events that fire during startup. LoginPage will show the modal and clear the flag.
  useEffect(() => {
    const handleSessionInvalidated = (event) => {
      logger.info('LoginContext: Session invalidated event received:', event.detail?.reason);

      // NED-4397: ignore an invalidation that belongs to a SUPERSEDED session
      // (its loginNonce is older than the current session's). Such an event is
      // the tail of a torn-down prior session landing after a newer login — it
      // must NOT tear down the live session (which would flicker through /login
      // and re-mount the whole authenticated tree) nor let LoginPage pop the
      // "Session Expired" modal over the app. Best-effort clear the stale flag
      // so a later transient /login mount can't consume it either.
      if (isSupersededInvalidation(event.detail, matrixAuthService.getLoginNonce())) {
        logger.info('LoginContext: ignoring superseded session-invalidated event');
        try { sessionStorage.removeItem('matrix:session-invalidated'); } catch { /* ignore */ }
        return;
      }

      // DON'T clear the flag here - let LoginPage show the modal first
      // LoginPage will clear the flag after displaying the message

      // Clear auth state - ProtectedRoute will redirect to /login
      setCurrentUser(null);
      setIsAuthenticated(false);
      setIsLoading(false);
    };

    window.addEventListener('matrix:session-invalidated', handleSessionInvalidated);
    return () => window.removeEventListener('matrix:session-invalidated', handleSessionInvalidated);
  }, []);

  // Resolve the homeserver classification once the user is authenticated.
  // timInformationService._fetch needs `matrixService.getClient()` to be
  // non-null, but LoginProvider is mounted above MatrixClientProvider so we
  // cannot observe client readiness via React context here. Poll the
  // singleton until it lights up, then hit the API.
  useEffect(() => {
    if (!isAuthenticated || !currentUser?.id) return;
    const domain = currentUser.id.split(':')[1];
    if (!domain) {
      setIsInsuredHomeserver(false);
      return;
    }
    const peeked = timInformationService.peekCache(domain);
    if (peeked !== null) {
      setIsInsuredHomeserver(peeked);
      return;
    }

    let cancelled = false;
    let pollTimer = null;
    const classify = async () => {
      while (!cancelled && !matrixService.getClient()) {
        await new Promise(resolve => {
          pollTimer = setTimeout(resolve, MATRIX_CLIENT_POLL_INTERVAL_MS);
        });
      }
      if (cancelled) return;
      try {
        const v = await timInformationService.isInsurance(domain);
        if (!cancelled) setIsInsuredHomeserver(!!v);
      } catch (err) {
        if (cancelled) return;
        // Fail-open at the value layer: once classification has tried and
        // failed, allow searches to proceed unfiltered. The privacy guarantee
        // applies only when we have positively identified the user as insured.
        logger.warn('[LoginContext] homeserver classification failed', { message: err?.message });
        setIsInsuredHomeserver(false);
      }
    };
    classify();

    // Dev-only hook for e2e tests: lets the test clear the
    // timInformationService cache and force a re-classification through
    // mocked /tim-information/v1/server/isInsurance routes after the page
    // has navigated. Gated on NODE_ENV so it never ships to production.
    if (process.env.NODE_ENV !== 'production') {
      window.__reclassifyHomeserver__ = async () => {
        timInformationService.clearCache();
        setIsInsuredHomeserver(null);
        try {
          const v = await timInformationService.isInsurance(domain);
          setIsInsuredHomeserver(!!v);
          return v;
        } catch {
          setIsInsuredHomeserver(false);
          return false;
        }
      };
    }

    return () => {
      cancelled = true;
      if (pollTimer) clearTimeout(pollTimer);
    };
  }, [isAuthenticated, currentUser?.id]);

  // Matrix login helper using auth service
  const performMatrixLogin = async (credentials, options = {}) => {
    const result = await matrixAuthService.login(credentials, options);
    
    if (result.success) {
      return result.credentials;
    } else {
      throw new Error(result.error?.message || 'Matrix login failed');
    }
  };

  const login = useCallback(async (userData, loginOptions = {}) => {
    try {
      setIsLoading(true);

      // A_25512 (Trennung von Mandanten): defence-in-depth on shared devices.
      // If any prior session state is present when login is invoked (because the
      // user navigated to /login directly, a tab was revived, or any other path
      // bypassed the logout button), tear it down completely before authenticating
      // the new actor. This guarantees the new actor cannot see the previous
      // actor's IndexedDB, blob URLs, or in-memory caches.
      // localStorage is the authoritative truth for "is there a session to clean
      // up." React state may be stale here; don't add it as a useCallback dep.
      let hasResidualSession = false;
      try {
        // Raw presence check (counts even a partial/corrupt blob as residual).
        hasResidualSession = credentialStore.hasCredentials();
      } catch (_) { /* private-mode or quota error — assume nothing to clean */ }
      if (hasResidualSession) {
        try {
          await matrixAuthService.logout({ skipServerLogout: true });
        } catch (cleanupErr) {
          logger.warn('A_25512 pre-login cleanup failed; proceeding cautiously:', cleanupErr);
        }
      }

      // Perform Matrix login
      const creds = await performMatrixLogin(
        { username: userData.username, password: userData.password },
        loginOptions,
      );
      
      // Create user object from Matrix credentials
      const userName = userData.username.startsWith('@') ? userData.username.split(':')[0].substring(1) : userData.username;
      const matrixUser = {
        id: creds.userId,
        name: userName,
        username: userData.username,
        matrixUserId: creds.userId,
        homeserver: creds.homeserverUrl,
        access_token: creds.accessToken, // Add access token for Matrix integration
        avatar: generateInitialsAvatar(userName), // Generate avatar from initials
      };
      
      // Clear any stale last activity timestamp from previous session
      // This ensures fresh auto-logout tracking for the new login
      try {
        localStorage.removeItem('ti_last_activity_timestamp');
      } catch (e) {
        logger.debug('Could not clear last activity timestamp on login:', e);
      }

      // Update state
      setCurrentUser(matrixUser);
      setIsAuthenticated(true);

      // NED-4397 (hygiene): clear any pre-existing session-invalidation flag so
      // a leftover can't be consumed by a later transient /login mount. Additive
      // only — does not replace the logout-path sessionStorage.clear().
      try { sessionStorage.removeItem('matrix:session-invalidated'); } catch { /* ignore */ }

      return { success: true, creds, user: matrixUser };
    } catch (error) {
      return { success: false, error: error.message };
    } finally {
      setIsLoading(false);
    }
  }, []);

  // SSO login — accepts the full LoginResponse from ssoService.exchangeLoginToken()
  const ssoLogin = useCallback(async (loginResponse, homeserverUrl = DEFAULT_HOMESERVER) => {
    try {
      setIsLoading(true);

      // A_25512 (Trennung von Mandanten): same defence-in-depth as password login.
      // Wipe any residual session state before the new actor authenticates.
      // localStorage is the authoritative truth for "is there a session to clean
      // up." React state may be stale here; don't add it as a useCallback dep.
      let hasResidualSession = false;
      try {
        // Raw presence check (counts even a partial/corrupt blob as residual).
        hasResidualSession = credentialStore.hasCredentials();
      } catch (_) { /* private-mode or quota error — assume nothing to clean */ }
      if (hasResidualSession) {
        try {
          await matrixAuthService.logout({ skipServerLogout: true });
        } catch (cleanupErr) {
          logger.warn('A_25512 pre-SSO-login cleanup failed; proceeding cautiously:', cleanupErr);
        }
      }

      // loginResponse = { access_token, device_id, user_id, refresh_token, expires_in_ms, well_known }
      await matrixAuthService.handleSSOCallback(
        loginResponse.access_token,
        loginResponse.device_id,
        {
          homeserver: homeserverUrl,
          userId: loginResponse.user_id,
          refreshToken: loginResponse.refresh_token,
          expiresInMs: loginResponse.expires_in_ms,
          wellKnown: loginResponse.well_known,
        },
      );

      // Cache SSO session for UIA flows
      cacheSsoSession(loginResponse.user_id);

      // Create user object (same pattern as password login)
      const userName = loginResponse.user_id.split(':')[0].substring(1);
      const matrixUser = {
        id: loginResponse.user_id,
        name: userName,
        username: loginResponse.user_id,
        matrixUserId: loginResponse.user_id,
        homeserver: homeserverUrl,
        access_token: loginResponse.access_token,
        avatar: generateInitialsAvatar(userName),
      };

      // Clear stale last activity timestamp
      try {
        localStorage.removeItem('ti_last_activity_timestamp');
      } catch (e) {
        logger.debug('Could not clear last activity timestamp on SSO login:', e);
      }

      // Update state
      setCurrentUser(matrixUser);
      setIsAuthenticated(true);

      // NED-4397 (hygiene): clear any pre-existing session-invalidation flag so
      // a leftover can't be consumed by a later transient /login mount.
      try { sessionStorage.removeItem('matrix:session-invalidated'); } catch { /* ignore */ }

      return { success: true, user: matrixUser };
    } catch (error) {
      logger.error('SSO login failed:', error);
      return { success: false, error: error.message };
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Note: authenticateUser and getOrCreateUser were removed as they're not used
  // The app now uses Matrix-only authentication via the login() function

  const logout = useCallback(async (reason) => {
    // Track if any IndexedDB deletion fails (will trigger page reload)
    let indexedDBDeletionFailed = false;

    try {
      setIsLoading(true);
      logger.info('LOGOUT: Starting logout from LoginContext');

      // Cancel all active browser notifications before Matrix client is destroyed
      try {
        const cancelledCount = notificationService.cancelAllNotifications();
        if (cancelledCount > 0) {
          logger.debug(`LOGOUT: Cancelled ${cancelledCount} active notification(s)`);
        }
      } catch (error) {
        // Don't block logout on notification cleanup errors
        logger.debug('LOGOUT: Failed to cancel notifications:', error);
      }

      // Clear FHIR/VZD tokens to prevent leakage on shared devices
      try {
        fhirService.clearTokens();
        logger.debug('LOGOUT: Cleared FHIR tokens');
      } catch (error) {
        logger.debug('LOGOUT: Failed to clear FHIR tokens:', error);
      }

      // Drop the homeserver-classification cache; the next user may have a
      // different homeserver, and the tri-state must restart at null so the
      // VZD search modal blocks until it is freshly resolved.
      try {
        timInformationService.clearCache();
        setIsInsuredHomeserver(null);
      } catch (error) {
        logger.debug('LOGOUT: Failed to clear homeserver classification cache:', error);
      }

      // Set reason flags early - matrixAuthService.logout() may trigger
      // matrix:session-invalidated events during cleanup, which causes
      // LoginPage to mount before we reach sessionStorage.clear() below
      if (reason === 'ssss_detection_failed') {
        sessionStorage.setItem('ssss_detection_failed', 'true');
      }

      // Logout from Matrix and clear all Matrix-related data
      logger.debug('LOGOUT: Calling matrixAuthService.logout()');
      const logoutResult = await matrixAuthService.logout();
      if (logoutResult?.indexedDBDeletionFailed) {
        indexedDBDeletionFailed = true;
        logger.warn('LOGOUT: Matrix IndexedDB deletion failed, will reload page');
      }

      // Clear app's IndexedDB database using the service
      try {
        logger.debug('LOGOUT: Deleting app IndexedDB database');
        const deleteResult = await indexedDBService.deleteDatabase();
        if (deleteResult && !deleteResult.success) {
          indexedDBDeletionFailed = true;
          logger.warn('LOGOUT: App IndexedDB deletion failed/blocked, will reload page');
        }
        logger.debug('LOGOUT: App database deleted');
      } catch (error) {
        indexedDBDeletionFailed = true;
        logger.error('LOGOUT: Failed to clear app database:', error);
      }

      // Double-check that all localStorage is cleared
      // (matrixAuthService.logout() should have done this, but ensure it's complete)
      // Settings are now stored in Matrix account data, not localStorage
      try {
        logger.debug('LOGOUT: Double-checking localStorage cleanup');
        const allLocalStorageKeys = Object.keys(localStorage);
        allLocalStorageKeys.forEach(key => {
          try {
            localStorage.removeItem(key);
          } catch (e) {
            logger.debug(`LOGOUT: Could not remove localStorage key ${key}:`, e);
          }
        });
      } catch (error) {
        logger.error('LOGOUT: Failed to clear all localStorage:', error);
      }

      // Clear all sessionStorage as well
      try {
        logger.debug('LOGOUT: Final sessionStorage clear from LoginContext');
        sessionStorage.clear();
        if (reason === 'inactivity') {
          // set afterward to show modal
          sessionStorage.setItem('logout_reason', 'inactivity');
        }
        // Re-set after sessionStorage.clear() wiped it — see early set at L273 for rationale
        if (reason === 'ssss_detection_failed') {
          sessionStorage.setItem('ssss_detection_failed', 'true');
        }
      } catch (error) {
        logger.error('LOGOUT: Failed to clear sessionStorage:', error);
      }

      // Reset state
      setCurrentUser(null);
      setIsAuthenticated(false);

      logger.info('LOGOUT: Completed - all data cleared from LoginContext');

      // If IndexedDB deletion failed, reload page so login can clear properly
      if (indexedDBDeletionFailed) {
        logger.info('LOGOUT: Reloading page due to IndexedDB deletion failure');
        // Set flag AFTER localStorage was cleared, so it persists across reload
        localStorage.setItem('matrixForceClearStores', 'true');
        // Small delay to ensure state updates propagate
        setTimeout(() => {
          window.location.reload();
        }, 100);
      }

      return { success: true };
    } catch (error) {
      logger.error('Logout error:', error);
      return { success: false, error: error.message };
    } finally {
      setIsLoading(false);
    }
  }, []);

  const value = {
    isAuthenticated,
    currentUser,
    setCurrentUser, // Export setCurrentUser for AvatarSync component
    isLoading,
    isInsuredHomeserver,
    login,
    ssoLogin,
    logout,
    setIsAuthenticated,
  };

  return (
    <LoginContext.Provider value={value}>
      {children}
    </LoginContext.Provider>
  );
};

LoginProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export default LoginContext;
